import { AfterViewInit, Component, EventEmitter, forwardRef, Input, OnInit, Output, SkipSelf, ViewChild } from '@angular/core';
import { ControlContainer, NgModel, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import Utils from '../../util';
import { AnessaTranslationService } from '../translation/anessa-translation.service';
import { UserpilotReloader } from '../userpilot-reloader.component';
import { MatOptionSelectionChange } from '@angular/material/core';
import { UnitValue } from '../../models/data-structures/unit-value.model';

@Component({
  selector: 'input-app',
  templateUrl: './input-app.component.html',
  styleUrls: ['./input-app.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputAppComponent),
      multi: true
    },
  ],
  viewProviders: [{
    provide: ControlContainer,
    useFactory: (container: ControlContainer) => container,
    deps: [[new SkipSelf(), ControlContainer]],
  }]
})

export class InputAppComponent extends UserpilotReloader implements OnInit, AfterViewInit {

  @ViewChild('matFormField') matFormField?: MatFormField;
  @ViewChild('valueControl') valueControl?: NgModel;

  @Input() name: string = '';
  @Input() inputType = 'number';
  @Input() selectOptions: string[] = [];
  translatedOptions: string[] = [];
  @Input() useClearPadding = true;
  @Input() useMaxWidth = false;
  @Input() disabled = false;
  @Input() inputSuffix = null;
  @Input() id = '';

  showClear = false;
  inputDisplayValue: string = '';
  @Input() inputValue: number | string = '';
  @Output() inputValueChange: EventEmitter<number | string> = new EventEmitter<number | string>();
  @Input() selectValue: string = '';
  @Output() selectValueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() onChage: EventEmitter<{ value: string | number, select: string }> = new EventEmitter<{ value: string | number, select: string }>();

  errorHint: string = '';

  // validation fields

  inputChecks: string[] = [];
  inputFormats: string[] = [];
  @Input() inputFormat?: string | string[];
  inputFormatsSet = {
    'not-negative': ['number', '>=0'],
    'percent': ['number', '>=0', '<=100'],
    'natural': ['number', 'integer', '>=1']
  };
  formatChecks = ['number', '>=0', '>=1', '<=100', 'integer'];

  @Input() coloredInput = false;
  @Input() defaultInputs: InputAppDefaultInputs = null;
  colorStyle = { blue: false, anessa_blue: false };

  customErrorMessage = '';
  setCustomErrorMessage(message: string) {
    this.customErrorMessage = message;
    // if (message) setTimeout(() => this.updateErrorMessage(message));
  }

  trigger?: string;

  constructor(private translationService: AnessaTranslationService) {
    super();
  }

  ngOnInit() {
    this.translationService.languageChangeNotifier
      //.pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.trigger = language;
      });
    // fill inputChecks and inputFormats
    if (this.inputFormat) {
      if (Array.isArray(this.inputFormat)) {
        for (const format of this.inputFormat) {
          this.addInputCheck(format);
        }
      } else {
        this.addInputCheck(this.inputFormat);
      }
    }
    // convert inputFormats into inputChecks
    for (const format of this.inputFormats) {
      if (format in this.inputFormatsSet) {
        if (Object.keys(this.inputFormatsSet).includes(format)) {
          const key = format as keyof typeof this.inputFormatsSet;
          for (const check of this.inputFormatsSet[key]) {
            if (!this.inputChecks.includes(check)) {
              this.inputChecks.push(check);
            }
          }
        }
      }
    }
  }

  addInputCheck(check: string) {
    if (this.formatChecks.includes(check)) {
      this.inputChecks.push(check);
    } else if (Object.keys(this.inputFormatsSet).includes(check)) {
      this.inputFormats.push(check);
    }
  }

  ngAfterViewInit() {
    if (this.valueExists(this.inputValue)) {
      this.inputDisplayValue = this.inputValue.toString();
    } else {
      const index = this.selectOptions.indexOf(this.selectValue);
      this.inputDisplayValue = this.translatedOptions[index];
    }
    if (this.matFormField) {
      const infix = this.matFormField._elementRef.nativeElement.querySelector('.mat-mdc-form-field-infix');
      infix.style.display = 'flex';
    }
    for (const option of this.selectOptions) {
      this.translatedOptions.push(this.translationService.get(option));
    }
    setTimeout(() => this.onInputChange());
  }

  onInputChange() {
    let value = (this.inputType === 'number') ? Utils.removeCommas(this.inputDisplayValue) : this.inputDisplayValue;
    if (!this.valueExists(value)) { value = ''; }
    if (this.translatedOptions.includes(this.inputDisplayValue)) {
      const index = this.translatedOptions.indexOf(this.inputDisplayValue);
      this.setSelectValue(this.selectOptions[index]);
    } else {
      this.setInputValue(value);
    }
    this.setInputsColors();
    this.onChage.emit({ value: this.inputValue, select: this.selectValue });
    this.updateValueValidity();
  }

  onInputFocusIn() {
    if (!this.inputDisplayValue) { return; }
    if (this.inputType === 'number') {
      const value = Utils.removeCommas(this.inputDisplayValue);
      this.inputDisplayValue = (this.valueExists(value)) ? value.toString().replace(/\,/gi, '') : '';
    }
  }

  onInputFocusOut() {
    if (this.valueExists(this.inputValue)) {
      if (this.inputType === 'number') {
        this.addCommas();
      }
    } else if (this.selectValue) {
      const index = this.selectOptions.indexOf(this.selectValue);
      this.inputDisplayValue = this.translatedOptions[index];
    }
  }

  valueExists(value: string | number): boolean {
    return Boolean(value || value === 0);
  }

  haveValue() {
    return this.valueExists(this.inputValue);
  }

  onSelect(event: MatOptionSelectionChange) {
    if (event.isUserInput) {
      const value = event.source.value;
      this.inputDisplayValue = this.translationService.get(value);
      this.setSelectValue(value);
    }
    this.onChage.emit({ value: this.inputValue, select: this.selectValue });
  }

  setInputValue(value: number | string) {
    if (this.valueExists(value)) {
      this.setSelectValue('');
    }
    this.inputValue = value;
    this.inputValueChange.emit(this.inputValue);
  }

  setSelectValue(value: string) {
    if (this.valueExists(value)) {
      this.setInputValue('');
    }
    this.selectValue = value;
    this.selectValueChange.emit(value);
  }

  addCommas() {
    const numberComponents = this.inputDisplayValue.split('.');
    const leftSide = numberComponents[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    numberComponents.splice(0, 1, leftSide);
    this.inputDisplayValue = numberComponents.join('.');
  }

  onClear() {
    this.setInputValue('');
    this.setSelectValue('');
    this.inputDisplayValue = '';
  }

  setInputsColors() {
    this.colorStyle = { blue: false, anessa_blue: false };
    if (!this.coloredInput) { return; }

    if (!(this.defaultInputs instanceof UnitValue)) {
      if (this.defaultInputs === this.inputValue) {
        this.colorStyle.blue = true;
        return
      }

      if (typeof this.defaultInputs === 'object' && this.defaultInputs !== null
        && this.defaultInputs.input === this.inputValue
        && this.defaultInputs.select === this.selectValue
      ) {
        this.colorStyle.blue = true;
        return;
      }
    }

    this.colorStyle.anessa_blue = true;
  }

  updateErrorMessage(message: string) {
    this.errorHint = this.translationService.get(message);
  }

  updateValueValidity() {
    if (this.valueControl && this.valueControl.control) {
      this.valueControl.control.updateValueAndValidity({ onlySelf: true });
      this.touchValueControl();
    }
  }

  touchValueControl() {
    if (this.valueControl && this.valueControl.control) {
      this.valueControl.control.markAsTouched();
    }
  }
}

type InputAppDefaultInputs = null | UnitValue | number | { input: number, select: string };